import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import Select from 'react-select';
import cogoToast from 'cogo-toast';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import Loader from '../General/Loader';
import ImageUploader from '../../ImageUpload/ImageUploader';
import {
  getRecommendationGrid,
  updateRecommendationGrid,
  getRecommendationGridPreview,
  deleteRecommendationRow,
  updateRecommendationRow
} from '../../api/recommendations';
import { getGMTTime } from '../../helpers/formatting';
import { RowFormModal } from './RowFormModal';
import { SortableRow } from './elements/SortableRow';
import { PreviewCards } from './elements/PreviewCards';
import './EditGrid.scss';

const statusOptions = [
  { label: 'Published', value: true },
  { label: 'Draft', value: false }
];

export const EditGrid = () => {
  const { id } = useParams();
  const history = useHistory();
  const sensors = useSensors(useSensor(PointerSensor));
  const currentUser = useSelector(state => state.users.currentUser);

  const [isLoading, setIsLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [published, setPublished] = useState(false);
  const [rows, setRows] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [showRowModal, setShowRowModal] = useState(false);

  // Promoted items state
  const [promotedItems, setPromotedItems] = useState([]);
  const [imageUploaderIndex, setImageUploaderIndex] = useState(null);

  // Preview state
  const [username, setUsername] = useState(currentUser?.username || '');
  const [submittedUsername, setSubmittedUsername] = useState('');
  const [gridPreview, setGridPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewMessage, setPreviewMessage] = useState('');
  const [useBatchAPI, setUseBatchAPI] = useState(false);

  useEffect(() => {
    fetchGrid();
  }, [id]);

  // Update username and submit when currentUser is loaded
  useEffect(() => {
    if (currentUser?.username && !submittedUsername) {
      setUsername(currentUser.username);
      setSubmittedUsername(currentUser.username);
    }
  }, [currentUser, submittedUsername]);

  // Fetch grid preview when submitted username, rows, or useBatchAPI changes
  useEffect(() => {
    const abortController = new AbortController();

    const fetchPreview = async () => {
      if (!submittedUsername || rows.length === 0) {
        setGridPreview(null);
        setPreviewMessage('');
        return;
      }

      try {
        setIsLoadingPreview(true);
        setPreviewMessage('');

        const response = await getRecommendationGridPreview(id, {
          username: submittedUsername,
          useBatchAPI
        });

        if (!abortController.signal.aborted) {
          setGridPreview(response.grid);

          if (!response.grid?.rows || response.grid.rows.every(row => !row.cards || row.cards.length === 0)) {
            setPreviewMessage('No recommendations found for this user');
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching preview:', error);
          setPreviewMessage('Error fetching grid preview');
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingPreview(false);
        }
      }
    };

    fetchPreview();

    return () => abortController.abort();
  }, [submittedUsername, rows, id, useBatchAPI]);

  const handleUsernameBlur = () => {
    if (username.trim() && username.trim() !== submittedUsername) {
      setSubmittedUsername(username.trim());
    }
  };

  const fetchGrid = async () => {
    try {
      setIsLoading(true);
      const grid = await getRecommendationGrid(id);

      setTitle(grid.title || '');
      setSlug(grid.slug || '');
      setPublished(Boolean(grid.published));
      setRows(grid.rows || []);
      setPromotedItems(grid.promotedItems || []);
    } catch (error) {
      console.error('Error fetching grid:', error);
      cogoToast.error('Error loading grid');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = rows.findIndex(item => item.id === active.id);
    const newIndex = rows.findIndex(item => item.id === over.id);

    const newRows = arrayMove(rows, oldIndex, newIndex);
    setRows(newRows);

    // After the move the row sits at newIndex, so its neighbors are newIndex-1 (above) and newIndex+1 (below).
    // Fallbacks place it before the first / after the last row when dropped at an edge.
    const aboveSort = newRows[newIndex - 1]?.sortOrderRank ?? -1 * getGMTTime();
    const belowSort = newRows[newIndex + 1]?.sortOrderRank ?? getGMTTime();
    const newSort = (aboveSort + belowSort) / 2;

    try {
      await updateRecommendationRow(active.id, {
        sortOrderRank: newSort
      });
      setRows(prev => prev.map(row => (row.id === active.id ? { ...row, sortOrderRank: newSort } : row)));
    } catch (error) {
      console.error('Error updating row order:', error);
      cogoToast.error('Error updating row order');
      setRows(rows);
    }
  };

  const handleEditRow = row => {
    setEditingRow(row);
    setShowRowModal(true);
  };

  const handleRowModalClose = async () => {
    setShowRowModal(false);
    setEditingRow(null);
    // Refetch grid to get updated or new rows
    await fetchGrid();
  };

  const handleRemoveRow = async rowId => {
    const confirmed = window.confirm('Are you sure you want to delete this row? This action cannot be undone.');

    if (!confirmed) {
      return;
    }

    try {
      await deleteRecommendationRow(rowId);
      setRows(prev => prev.filter(row => row.id !== rowId));
      cogoToast.success('Row deleted successfully');
    } catch (error) {
      console.error('Error deleting row:', error);
      cogoToast.error('Error deleting row');
    }
  };

  const handleAddNewRow = () => {
    setEditingRow(null);
    setShowRowModal(true);
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!title.trim()) {
      cogoToast.error('Please enter a title');
      return;
    }

    if (!slug.trim()) {
      cogoToast.error('Please enter a slug');
      return;
    }

    try {
      setIsSubmitting(true);

      const filteredPromotedItems = promotedItems.filter(item => item.type && item.id);
      const gridData = {
        title: title.trim(),
        slug: slug.trim(),
        published,
        promotedItems: filteredPromotedItems.length ? filteredPromotedItems : null
      };

      await updateRecommendationGrid(id, gridData);
      cogoToast.success('Recommendation grid updated successfully');
      history.push('/recommendations');
    } catch (error) {
      console.error('Error updating recommendation grid:', error);
      cogoToast.error('Error updating recommendation grid');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddPromotedItem = () => {
    setPromotedItems([...promotedItems, { type: 'Collection', id: '', name: '', title: '', buttonLabel: '', image: '' }]);
  };

  const handleUpdatePromotedItem = (index, field, value) => {
    const updated = [...promotedItems];
    updated[index] = { ...updated[index], [field]: field === 'id' ? parseInt(value, 10) || '' : value };
    setPromotedItems(updated);
  };

  const handleRemovePromotedItem = index => {
    setPromotedItems(promotedItems.filter((_, i) => i !== index));
  };

  const handleCancel = () => {
    history.push('/recommendations');
  };

  if (isLoading) {
    return (
      <div className='edit-grid-container'>
        <div className='loading-container'>
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className='edit-grid-container'>
        <div className='header-section'>
          <h1>Edit Recommendation Grid</h1>
        </div>

        <form onSubmit={handleSubmit} className='edit-grid-form'>
          <div className='form-content'>
            <div className='form-grid'>
              <div className='form-field'>
                <label htmlFor='title'>Title</label>
                <input
                  id='title'
                  name='title'
                  type='text'
                  placeholder='Enter grid title'
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>

              <div className='form-field'>
                <label htmlFor='slug'>Slug</label>
                <input
                  id='slug'
                  name='slug'
                  type='text'
                  placeholder='Enter slug (e.g., home-tab)'
                  value={slug}
                  onChange={e => setSlug(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>

              <div className='form-field'>
                <label htmlFor='published'>Status</label>
                <Select
                  id='published'
                  name='published'
                  options={statusOptions}
                  value={statusOptions.find(option => option.value === published) || statusOptions[1]}
                  onChange={option => setPublished(option.value)}
                  isDisabled={isSubmitting}
                  placeholder='Select status'
                />
              </div>
            </div>

            <div className='promotion-section'>
              <div className='promotion-header'>
                <label>Promoted Items (optional)</label>
              </div>
              {promotedItems.length > 0 && (
                <div className='promotion-items-list'>
                  {promotedItems.map((item, index) => (
                    <div key={index} className='promotion-item-card'>
                      <div className='promotion-item-row'>
                        <Select
                          className='promotion-type-select'
                          options={[{ label: 'Collection', value: 'Collection' }]}
                          value={{ label: item.type, value: item.type }}
                          onChange={option => handleUpdatePromotedItem(index, 'type', option.value)}
                          isDisabled={isSubmitting}
                          placeholder='Select type'
                        />
                        <input
                          type='text'
                          className='promotion-id-input'
                          placeholder='Enter ID'
                          value={item.id || ''}
                          onChange={e => handleUpdatePromotedItem(index, 'id', e.target.value)}
                          disabled={isSubmitting}
                        />
                        <button
                          type='button'
                          className='remove-promotion-button'
                          onClick={() => handleRemovePromotedItem(index)}
                          disabled={isSubmitting}
                          title='Remove item'
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                      <div className='promotion-item-fields'>
                        <label className='promotion-item-fields-header'>Overrides</label>
                        <div className='promotion-field'>
                          <label>Preamble</label>
                          <input
                            type='text'
                            placeholder='Defaults to "Curated by [user]"'
                            value={item.title || ''}
                            onChange={e => handleUpdatePromotedItem(index, 'title', e.target.value)}
                            disabled={isSubmitting}
                          />
                        </div>
                        <div className='promotion-field'>
                          <label>Collection name</label>
                          <input
                            type='text'
                            placeholder='Defaults to the collection name'
                            value={item.name || ''}
                            onChange={e => handleUpdatePromotedItem(index, 'name', e.target.value)}
                            disabled={isSubmitting}
                          />
                        </div>
                        <div className='promotion-field'>
                          <label>Button Label</label>
                          <input
                            type='text'
                            placeholder='e.g. "SHOP"'
                            value={item.buttonLabel || ''}
                            onChange={e => handleUpdatePromotedItem(index, 'buttonLabel', e.target.value)}
                            disabled={isSubmitting}
                          />
                        </div>
                        <div className='promotion-field'>
                          <label>Image</label>
                          <div className='promotion-image-field'>
                            {item.image && <img className='promotion-image-preview' src={item.image} alt='Banner' />}
                            <button
                              type='button'
                              className='upload-image-button'
                              onClick={() => setImageUploaderIndex(index)}
                              disabled={isSubmitting}
                            >
                              {item.image ? 'Change Image' : 'Upload Image'}
                            </button>
                            {item.image && (
                              <button
                                type='button'
                                className='clear-image-button'
                                onClick={() => handleUpdatePromotedItem(index, 'image', '')}
                                disabled={isSubmitting}
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <button type='button' className='add-promotion-button' onClick={handleAddPromotedItem} disabled={isSubmitting}>
                Add Item
              </button>
            </div>

            <div className='rows-section'>
              <div className='rows-header'>
                <label>Rows</label>
                <button type='button' className='add-row-button' onClick={handleAddNewRow} disabled={isSubmitting}>
                  Add New Row
                </button>
              </div>
              {rows.length === 0 ? (
                <div className='empty-rows'>No rows in this grid</div>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={rows} strategy={verticalListSortingStrategy}>
                    <div className='rows-list'>
                      {rows.map(row => (
                        <SortableRow key={row.id} row={row} onEdit={handleEditRow} onRemove={handleRemoveRow} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>

            {rows.length > 0 && (
              <div className='preview-section'>
                <div className='preview-controls'>
                  <div className='form-field'>
                    <label htmlFor='username'>Preview for User</label>
                    <input
                      id='username'
                      name='username'
                      type='text'
                      placeholder='Enter username to preview recommendations'
                      value={username}
                      onChange={e => setUsername(e.target.value)}
                      onBlur={handleUsernameBlur}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className='form-field checkbox-field'>
                    <input
                      id='useBatchAPI'
                      name='useBatchAPI'
                      type='checkbox'
                      checked={useBatchAPI}
                      onChange={e => setUseBatchAPI(e.target.checked)}
                      disabled={isSubmitting}
                    />
                    <label htmlFor='useBatchAPI'>Use Batch API (experimental)</label>
                  </div>
                </div>

                <div className='grid-preview-display'>
                  {gridPreview && gridPreview.rows && gridPreview.rows.length > 0 ? (
                    gridPreview.rows
                      .filter(row => row.definitionType === 'ML')
                      .map((row, rowIndex) => (
                        <div key={rowIndex} className='row-section'>
                          <h3 className='row-title'>
                            {row.title}
                            <span className='row-count'>({row.cards?.length || 0})</span>
                          </h3>
                          <PreviewCards
                            cards={row.cards}
                            isLoading={isLoadingPreview && rowIndex === 0}
                            message={rowIndex === 0 ? previewMessage : ''}
                            emptyMessage='No recommendations for this row'
                          />
                        </div>
                      ))
                  ) : (
                    <PreviewCards
                      cards={[]}
                      isLoading={isLoadingPreview}
                      message={previewMessage}
                      emptyMessage='Enter a username to preview recommendations'
                    />
                  )}

                  {(gridPreview?.rows || []).some(row => row.definitionType !== 'ML') && (
                    <div className='info-message'>Note: "Product" rows are currently not shown in the preview.</div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className='form-actions'>
            <button type='button' className='cancel-button' onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </button>
            <button type='submit' className='save-button' disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {showRowModal && <RowFormModal close={handleRowModalClose} onSuccess={handleRowModalClose} editingRow={editingRow} gridId={id} />}

      <ImageUploader
        isVisible={imageUploaderIndex !== null}
        setIsVisible={visible => {
          if (!visible) setImageUploaderIndex(null);
        }}
        initialImageUrl={imageUploaderIndex !== null ? promotedItems[imageUploaderIndex]?.image || '' : ''}
        onSaveCallback={url => {
          handleUpdatePromotedItem(imageUploaderIndex, 'image', url);
        }}
        previewAspectRatio={1.5}
      />
    </>
  );
};
